import React from 'react';
import { ChevronLeftIcon, ClockIcon, UserIcon, EyeIcon, PaperclipIcon, TagIcon, DownloadIcon, MessageSquareIcon, FileIcon } from 'lucide-react';
import { Note, NoteHistoryEntry } from './NotesManagement';
import { StatusBadge } from '../../StatusBadge';
interface NoteDetailProps {
  note: Note;
  history: NoteHistoryEntry[];
  onBack: () => void;
  locale: string;
}
export function NoteDetail({
  note,
  history,
  onBack,
  locale
}: NoteDetailProps) {
  // Format timestamp for display
  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  // Render visibility badge
  const renderVisibilityBadge = (visibility: string) => {
    switch (visibility) {
      case 'Public':
        return <StatusBadge status="success" text="Public" data-id="element-17506" />;
      case 'Role-based':
        return <StatusBadge status="warning" text="Role-based" data-id="element-17507" />;
      case 'User-specific':
        return <StatusBadge status="info" text="User-specific" data-id="element-17508" />;
      case 'Creator-only':
        return <StatusBadge status="error" text="Private" data-id="element-17509" />;
      default:
        return <StatusBadge status="default" text={visibility} data-id="element-17510" />;
    }
  };
  // Render history action badge
  const renderActionBadge = (action: string) => {
    switch (action) {
      case 'Created':
        return <StatusBadge status="success" text="Created" data-id="element-17511" />;
      case 'Modified':
        return <StatusBadge status="warning" text="Modified" data-id="element-17512" />;
      case 'Visibility Changed':
        return <StatusBadge status="info" text="Visibility Changed" data-id="element-17513" />;
      case 'Attachment Added':
        return <StatusBadge status="success" text="Attachment Added" data-id="element-17514" />;
      case 'Attachment Removed':
        return <StatusBadge status="error" text="Attachment Removed" data-id="element-17515" />;
      default:
        return <StatusBadge status="default" text={action} data-id="element-17516" />;
    }
  };
  return <div className="space-y-6" data-id="element-17517">
      {/* Back button */}
      <div data-id="element-17518">
        <button onClick={onBack} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-17519">
          <ChevronLeftIcon className="h-4 w-4 mr-2" data-id="element-17520" />
          Back to Notes
        </button>
      </div>
      {/* Note details card */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-17521">
        <div className="px-4 py-5 sm:px-6 flex justify-between items-start" data-id="element-17522">
          <div data-id="element-17523">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-17524">
              Note Details
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-17525">
              Full information and history for this note.
            </p>
          </div>
          <div className="flex items-center" data-id="element-17526">
            <span className="mr-2 text-sm text-gray-500" data-id="element-17527">
              {formatTimestamp(note.createdAt)}
            </span>
            {renderVisibilityBadge(note.visibility)}
          </div>
        </div>
        {/* Module reference */}
        <div className="border-t border-gray-200 px-4 py-5 sm:px-6" data-id="element-17528">
          <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2" data-id="element-17529">
            <div className="sm:col-span-1" data-id="element-17530">
              <dt className="text-sm font-medium text-gray-500" data-id="element-17531">
                Module Reference
              </dt>
              <dd className="mt-1 text-sm text-gray-900" data-id="element-17532">
                <span className="font-medium text-indigo-600" data-id="element-17533">
                  {note.moduleType} #{note.moduleId}
                </span>
                <span className="block text-gray-500" data-id="element-17534">{note.moduleName}</span>
              </dd>
            </div>
            <div className="sm:col-span-1" data-id="element-17535">
              <dt className="text-sm font-medium text-gray-500" data-id="element-17536">Note Type</dt>
              <dd className="mt-1 text-sm text-gray-900" data-id="element-17537">
                {note.noteTypeName}
              </dd>
            </div>
            <div className="sm:col-span-1" data-id="element-17538">
              <dt className="text-sm font-medium text-gray-500" data-id="element-17539">Author</dt>
              <dd className="mt-1 text-sm text-gray-900 flex items-center" data-id="element-17540">
                <UserIcon className="h-4 w-4 text-gray-400 mr-1" data-id="element-17541" />
                {note.authorName}
              </dd>
            </div>
            <div className="sm:col-span-1" data-id="element-17542">
              <dt className="text-sm font-medium text-gray-500" data-id="element-17543">Visibility</dt>
              <dd className="mt-1 text-sm text-gray-900 flex items-center" data-id="element-17544">
                <EyeIcon className="h-4 w-4 text-gray-400 mr-1" data-id="element-17545" />
                {note.visibility}
                {note.visibilityDetails && <span className="ml-1 text-gray-500" data-id="element-17546">
                    ({note.visibilityDetails})
                  </span>}
              </dd>
            </div>
            {(note.entityName || note.siteName) && <div className="sm:col-span-2" data-id="element-17547">
                <dt className="text-sm font-medium text-gray-500" data-id="element-17548">Location</dt>
                <dd className="mt-1 text-sm text-gray-900" data-id="element-17549">
                  {note.entityName && <span className="mr-2" data-id="element-17550">Entity: {note.entityName}</span>}
                  {note.siteName && <span data-id="element-17551">Site: {note.siteName}</span>}
                </dd>
              </div>}
            <div className="sm:col-span-2" data-id="element-17552">
              <dt className="text-sm font-medium text-gray-500" data-id="element-17553">
                Note Content
              </dt>
              <dd className="mt-1 text-sm text-gray-900 p-3 bg-gray-50 rounded border border-gray-200 whitespace-pre-wrap" data-id="element-17554">
                {note.body}
              </dd>
            </div>
            {note.tags && note.tags.length > 0 && <div className="sm:col-span-2" data-id="element-17555">
                <dt className="text-sm font-medium text-gray-500" data-id="element-17556">Tags</dt>
                <dd className="mt-1" data-id="element-17557">
                  <div className="flex flex-wrap gap-2" data-id="element-17558">
                    {note.tags.map((tag, index) => <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800" data-id="element-17559">
                        <TagIcon className="h-3 w-3 mr-1" data-id="element-17560" />
                        {tag}
                      </span>)}
                  </div>
                </dd>
              </div>}
            {note.hasAttachment && <div className="sm:col-span-2" data-id="element-17561">
                <dt className="text-sm font-medium text-gray-500" data-id="element-17562">
                  Attachment
                </dt>
                <dd className="mt-1 text-sm text-gray-900" data-id="element-17563">
                  <div className="flex items-center" data-id="element-17564">
                    <FileIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-17565" />
                    <span data-id="element-17566">{note.attachmentName}</span>
                    <button className="ml-2 inline-flex items-center px-2 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-indigo-500" data-id="element-17567">
                      <DownloadIcon className="h-3 w-3 mr-1" data-id="element-17568" />
                      Download
                    </button>
                  </div>
                </dd>
              </div>}
          </dl>
        </div>
      </div>
      {/* Note history */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-17569">
        <div className="px-4 py-5 sm:px-6" data-id="element-17570">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-17571">
            Note History
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-17572">
            Audit trail of changes to this note.
          </p>
        </div>
        <div className="border-t border-gray-200" data-id="element-17573">
          <ul className="divide-y divide-gray-200" data-id="element-17574">
            {history.length === 0 ? <li className="px-4 py-4 sm:px-6 text-center text-sm text-gray-500" data-id="element-17575">
                No history available for this note.
              </li> : history.map(entry => <li key={entry.id} className="px-4 py-4 sm:px-6" data-id="element-17576">
                  <div className="flex items-center justify-between" data-id="element-17577">
                    <div className="flex items-center" data-id="element-17578">
                      <ClockIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-17579" />
                      <div data-id="element-17580">
                        <div className="flex items-center" data-id="element-17581">
                          {renderActionBadge(entry.action)}
                          <span className="ml-2 text-sm text-gray-500" data-id="element-17582">
                            {formatTimestamp(entry.timestamp)}
                          </span>
                        </div>
                        <div className="text-sm text-gray-500" data-id="element-17583">
                          By: {entry.userName}
                        </div>
                        {entry.details && <div className="mt-1 text-sm text-gray-500" data-id="element-17584">
                            {entry.details}
                          </div>}
                      </div>
                    </div>
                  </div>
                </li>)}
          </ul>
        </div>
      </div>
    </div>;
}